/**
 * Phase H1-η · useConfirmationId
 *
 * Freezes the idempotency-UUID lifecycle mandated by scope §7 · D-η6:
 *
 *   Idle → arm() → Armed (UUID minted, disable_submit=true) → succeed()
 *          → Retired.
 *
 *   Cancel-and-reopen mints a fresh UUID.
 *   Retry same attempt reuses UUID.
 *   Material edit after failure mints a new UUID (via fingerprint change).
 *   Double-click disables submit immediately.
 *
 * Consumers:
 *   const { confirmation_id, arm, succeed, reset, disable_submit } =
 *       useConfirmationId({ fingerprint });
 *
 *   const onConfirm = async () => {
 *     const id = arm();
 *     try { await post({ ...payload, confirmation_id: id }); succeed(); }
 *     catch (e) { /* stay armed for retry — UUID persists */ /* }
 *   };
 */
import { useCallback, useMemo, useRef, useState } from "react";

// Feature-detect crypto.randomUUID (Node/JSDom test envs may lack it)
const _mint = () => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch (_e) { /* fall through */ }
  // Fallback: RFC-4122-style hex (not cryptographically strong; only for tests)
  const hex = () => Math.floor((1 + Math.random()) * 0x1000000).toString(16).slice(1);
  return `${hex()}${hex()}-${hex()}-${hex()}-${hex()}-${hex()}${hex()}${hex()}`;
};

export default function useConfirmationId({ fingerprint } = {}) {
  const [confirmationId, setConfirmationId] = useState(null);
  const [inFlight, setInFlight] = useState(false);
  const armedFingerprint = useRef(null);

  const arm = useCallback(() => {
    // Fingerprint change ⇒ new logical attempt ⇒ new UUID.
    if (armedFingerprint.current !== fingerprint || !confirmationId) {
      const id = _mint();
      setConfirmationId(id);
      armedFingerprint.current = fingerprint;
      setInFlight(true);
      return id;
    }
    // Same attempt reused (retry) — keep UUID.
    setInFlight(true);
    return confirmationId;
  }, [confirmationId, fingerprint]);

  const succeed = useCallback(() => {
    setConfirmationId(null);
    armedFingerprint.current = null;
    setInFlight(false);
  }, []);

  const reset = useCallback(() => {
    setConfirmationId(null);
    armedFingerprint.current = null;
    setInFlight(false);
  }, []);

  const settle = useCallback(() => {
    // Called after any HTTP response (success or failure) so the
    // submit button re-enables — but the UUID stays armed for retry.
    setInFlight(false);
  }, []);

  const disableSubmit = useMemo(() => inFlight, [inFlight]);

  return {
    confirmation_id: confirmationId,
    arm,
    succeed,
    reset,
    settle,
    disable_submit: disableSubmit,
  };
}
